import { isNullOrUndefined } from 'src/app/shared/extensions/extensions';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, UntypedFormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { FormInputError } from '../_model/form-input.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaskConfig } from './mask/index';
import { MaskedInputDirective, TextMaskModule } from '@matheo/text-mask';
import { CommonModule } from '@angular/common';
import { SelectableInputModule } from 'src/app/shared/selectable-input/selectable-input.module';
import { ClickOutsideModule } from 'ng-click-outside';
import { BrowserModule } from '@angular/platform-browser';
import { TriggerClickDirective } from 'src/app/shared/directives/trigger-click/trigger-click.directive';

const DEFAULT_INPUT_ID_PREFIX = 'form-input';

@Component({
  selector: 'app-form-input',
  templateUrl: './form-input.component.html',
  styleUrls: ['./form-input.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    TextMaskModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormInputComponent),
      multi: true
    }
  ]
})
export class FormInputComponent implements OnInit, AfterViewChecked, ControlValueAccessor {
  constructor(
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) { }
  preValue = '';
  el: HTMLInputElement;
  MaskConfig = MaskConfig;
  value: string = null;

  dynamicAutocomplete = 'nope-' + Math.random().toString(36).substring(2);
  isReadonlyHack = true;

  onChange: (value) => void;
  onTouched: () => void;

  @ViewChild(MaskedInputDirective) textMask: MaskedInputDirective;
  @ViewChild('input') input: ElementRef;
  @Output() onPaste = new EventEmitter<string>();
  @Output() blured = new EventEmitter<Event>();
  @Input() disabled: boolean;
  @Input() inputId: string;
  @Input() label: string;
  @Input() formControlName: string;
  @Input() formGroup: UntypedFormGroup;
  @Input() disableBlurChange: boolean = false;
  @Input() isRequired: Boolean;
  @Input() placeholder: string;
  @Input() errorList: FormInputError[];
  @Input() type = 'text';
  @Input() maxLength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() step = 1;
  @Input() subLabel: string;
  @Input() subLabelSpaceing = false;
  @Input() mask;
  @Input() wrapSubLabel = false;
  @Input() info: string;
  @Input() hint: string;
  @Input() isReportedForCorrection = false;
  @Input() warnValue: any;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';
  @Input() onClick: () => void = () => { };
  generatedInputId: string;

  removeReadonlyHack(): void {
    this.isReadonlyHack = false;
  }

  ngOnInit() {
    this.generatedInputId = this.getInputId();
    if (this.errorList) {
      this.errorList.forEach((error) => {
        error.label = error.key ? this.translate.instant(error.key) : error.label;
      });
    }
  }

  ngAfterViewChecked() {
    if (this.mask === 'date' || this.mask === 'accountNumber') {
      this.el = this.elementRef.nativeElement;
      this.el.setAttribute('autocomplete', this.dynamicAutocomplete);
    }
  }

  onInputMask($event: Event) {
    const target = $event.target as HTMLInputElement;
    if (this.mask === 'digit') {
      target.value = target.value.replace(/\D/g, '');
    }
    this.preValue = target.value;
    this.value = target.value;
    this.onChange(target.value);
  }

  onPropagateBlur($event: Event) {
    const target = $event.target as HTMLInputElement;
    target.value = target.value.trim();
    if (!this.disableBlurChange) {
      this.onChange(target.value);
    }
    this.onTouched();
    this.blured.emit($event);
  }

  writeValue(value: string): void {
    this.preValue = value ? value : '';
    this.value = value ? value : '';
    setTimeout(() => this.updateTextMask(value), 0);
  }

  updateTextMask(value: string): void {
    if (this.mask === 'date' || this.mask === 'accountNumber') {
      this.textMask.writeValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onHasOneOfErrors(errors: string[]) {
    let invalid = false;
    errors.forEach((value) => {
      if (this.formGroup.controls[this.formControlName].hasError(value)) {
        invalid = true;
      }
    });
    return this.isTouched() && invalid;
  }

  incrementValue(): void {
    if (isNullOrUndefined(this.value) || this.value === '') {
      this.value = '0';
    }

    let value = parseFloat(this.value);
    if (isNullOrUndefined(this.max) || value < this.max) {
      value += this.step;
    }
    this.writeValue(value.toString());
    this.onChange(this.value);
  }

  decrementValue(): void {
    if (isNullOrUndefined(this.value) || this.value === '') {
      this.value = '0';
    }

    let value = parseFloat(this.value);
    if (isNullOrUndefined(this.min) || value > this.min) {
      value -= this.step;
    }
    this.writeValue(value.toString());
    this.onChange(this.value);
  }

  showWarning(): boolean {
    return !!this.warnValue && this.isTouched() && this.warnValue === this.value;
  }

  showHint(): boolean {
    return (
      !!this.hint &&
      !this.showWarning() &&
      !this.errorList?.some((error) => this.onHasOneOfErrors(error.type))
    );
  }

  isTouched() {
    return this.formGroup?.controls[this.formControlName].touched;
  }

  isValid() {
    return this.formGroup?.controls[this.formControlName].disabled
      ? true
      : this.formGroup?.controls[this.formControlName].valid;
  }

  handleOnPaste(event: ClipboardEvent): void {
    this.onPaste.emit(event.clipboardData.getData('text/plain'));
  }

  private getInputId(): string {
    if (this.inputId) {
      return this.inputId;
    }

    const controlName = this.formControlName || 'input';
    return `${DEFAULT_INPUT_ID_PREFIX}-${controlName}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
